/** Bandeiras com ícone em `~/assets/icons/CreditCard`. */
export type CardBrand = 'visa' | 'mastercard' | 'elo' | 'amex' | 'diners' | 'hipercard' | 'unknown';

const digitsOf = (value: string | null | undefined) => (value ?? '').replace(/\D/g, '');

/** Faixas de BIN Elo (checadas antes de Visa/Mastercard, que compartilham prefixos). */
const ELO_RANGES: Array<[number, number]> = [
  [401178, 401179],
  [431274, 431274],
  [438935, 438935],
  [451416, 451416],
  [457393, 457393],
  [457631, 457632],
  [504175, 504175],
  [506699, 506778],
  [509000, 509999],
  [627780, 627780],
  [636297, 636297],
  [636368, 636368],
  [650031, 650033],
  [650035, 650051],
  [650405, 650439],
  [650485, 650538],
  [650541, 650598],
  [650700, 650718],
  [650720, 650727],
  [650901, 650920],
  [651652, 651679],
  [655000, 655019],
  [655021, 655058]
];

const BRAND_NAMES: Record<CardBrand, string> = {
  visa: 'Visa',
  mastercard: 'Mastercard',
  elo: 'Elo',
  amex: 'American Express',
  diners: 'Diners Club',
  hipercard: 'Hipercard',
  unknown: 'Cartão'
};

/** Bandeira a partir do número (BIN). Só para exibição: quem valida de verdade é o gateway. */
export function detectCardBrand(number: string | null | undefined): CardBrand {
  const digits = digitsOf(number);
  if (!digits) return 'unknown';
  const bin = Number(digits.slice(0, 6).padEnd(6, '0'));
  if (digits.length >= 6 && ELO_RANGES.some(([from, to]) => bin >= from && bin <= to)) return 'elo';
  if (/^(606282|3841)/.test(digits)) return 'hipercard';
  if (/^3[47]/.test(digits)) return 'amex';
  if (/^(30[0-5]|36|38)/.test(digits)) return 'diners';
  if (/^4/.test(digits)) return 'visa';
  const two = Number(digits.slice(0, 2));
  const four = Number(digits.slice(0, 4));
  if ((two >= 51 && two <= 55) || (four >= 2221 && four <= 2720)) return 'mastercard';
  return 'unknown';
}

export function getCardBrandName(brand: CardBrand | string | null | undefined): string {
  const key = String(brand ?? '').toLowerCase() as CardBrand;
  return BRAND_NAMES[key] ?? BRAND_NAMES.unknown;
}

export function getLastFourDigits(number: string | null | undefined): string {
  return digitsOf(number).slice(-4);
}

/** «Mastercard **** 1234» — enquanto a API não traz o emissor, mostramos a bandeira. */
export function formatCardLabel(brand: CardBrand | string | null | undefined, lastFour: string): string {
  return `${getCardBrandName(brand)} **** ${lastFour}`;
}

/** Luhn com 13 a 19 dígitos. */
export function isCardNumberValid(number: string | null | undefined): boolean {
  const digits = digitsOf(number);
  if (digits.length < 13 || digits.length > 19) return false;
  let sum = 0;
  for (let i = 0; i < digits.length; i += 1) {
    let n = Number(digits[digits.length - 1 - i]);
    if (i % 2 === 1) {
      n *= 2;
      if (n > 9) n -= 9;
    }
    sum += n;
  }
  return sum % 10 === 0;
}

/** Validade `MM/AA`: mês 01–12 e não vencida (o mês corrente ainda vale). */
export function isCardExpiryValid(expiry: string | null | undefined, now: Date = new Date()): boolean {
  const match = /^(\d{2})\/(\d{2})$/.exec((expiry ?? '').trim());
  if (!match) return false;
  const month = Number(match[1]);
  const year = 2000 + Number(match[2]);
  if (month < 1 || month > 12) return false;
  const current = now.getFullYear() * 12 + now.getMonth();
  return year * 12 + (month - 1) >= current;
}

/** Bandeira vinda da API (`brand` de `Customers/{id}/cards`, «MasterCard», «American Express»…) no formato interno. */
export function toCardBrand(raw: string | null | undefined): CardBrand {
  const value = String(raw ?? '').toLowerCase();
  if (value.includes('master')) return 'mastercard';
  if (value.includes('visa')) return 'visa';
  if (value.includes('amex') || value.includes('american')) return 'amex';
  if (value.includes('elo')) return 'elo';
  if (value.includes('hiper')) return 'hipercard';
  if (value.includes('diners')) return 'diners';
  return 'unknown';
}
